import React, { memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { uncompletedLoanErrorMessageParams } from 'app/pages/loanApplication/LoanApplication.constants';
import PrimaryDataWrapper from 'app/shared/components/loanManagement/PrimaryDataWrapper';
import { ModalSize } from 'app/shared/componentsRestyled/coreDesignSystem/Modals';
import { WithLoadingPropTypes } from 'hocs/withLoading';

import useLoanId from 'hooks/useLoanId';
import usePendingChangeAuthors from 'hooks/usePendingChangeAuthors';
import { getViewDataChanges } from 'utils/loanApplication';

import { openDialogAction, showMessageAction } from 'store/global/actions';
import { showMessageThatCurrentUserCantChangeLoanAction } from 'store/loanApplications/actions';
import {
  selectEntityChangedPropsById,
  selectIsCurrentUserCanChangeLoan,
  selectIsLoanApplicationEditAllowed,
  selectIsLoanCanceled,
  selectIsLoanInitiatedWithErrors,
  selectUnderwritingNotes,
} from 'store/loanApplications/selectors';

import { UnderwritingTestIds } from '../../Underwriting.testIds';

import NotesForm from './NotesForm';
import { getNotesViewConfig, notesViewConfigFields } from './NotesView.config';

const Notes = ({ isLoading }) => {
  const dispatch = useDispatch();
  const loanId = useLoanId();

  const underwritingNotes = useSelector(selectUnderwritingNotes);

  const isLoanInitedWithErrors = useSelector(selectIsLoanInitiatedWithErrors);
  const isCurrentUserCanChangeLoan = useSelector(selectIsCurrentUserCanChangeLoan);
  const isLoanCanceled = useSelector(selectIsLoanCanceled);
  const isEditAllowed = useSelector(selectIsLoanApplicationEditAllowed) && !isLoanCanceled;
  const pendingChangesData = useSelector(selectEntityChangedPropsById(loanId));
  const pendingChangeAuthors = usePendingChangeAuthors(loanId);

  const onEditNote = useCallback(() => {
    if (isLoanInitedWithErrors) {
      return dispatch(showMessageAction(uncompletedLoanErrorMessageParams));
    }

    if (!isCurrentUserCanChangeLoan) {
      return dispatch(showMessageThatCurrentUserCantChangeLoanAction());
    }

    dispatch(
      openDialogAction({
        header: 'Edit notes',
        size: ModalSize.XL,
        body: dialogProps => <NotesForm notes={underwritingNotes} {...dialogProps} />,
      }),
    );
  }, [dispatch, underwritingNotes, isCurrentUserCanChangeLoan, isLoanInitedWithErrors]);

  const notesViewConfig = useMemo(
    () =>
      getNotesViewConfig({
        onEdit: onEditNote,
        isEditAllowed,
        isLoading,
      }),
    [onEditNote, isEditAllowed, isLoading],
  );

  const formattedForViewFormValues = useMemo(
    () => ({
      underwritingManualNotes: underwritingNotes,
    }),
    [underwritingNotes],
  );

  const notesDataChanges = useMemo(
    () =>
      getViewDataChanges({
        pendingChangesData,
        configFields: notesViewConfigFields,
      }),
    [pendingChangesData],
  );

  return (
    <PrimaryDataWrapper
      config={notesViewConfig}
      data={underwritingNotes ? formattedForViewFormValues : null}
      dataAuthors={pendingChangeAuthors}
      dataChanges={notesDataChanges}
      dataTestId={UnderwritingTestIds.NOTES_DATA_BUTTON}
      isButtonsVisible={isEditAllowed}
    />
  );
};

Notes.propTypes = {
  ...WithLoadingPropTypes,
};

export default memo(Notes);
